"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { FormEvent, useState } from "react";
import type { Locale } from "@/lib/types";

const text = {
  en: {
    eyebrow: "ALSFRS-R AI",
    title: "Login to continue",
    username: "Username",
    password: "Password",
    language: "Assessment language",
    submit: "Sign in",
    loading: "Signing in…",
    policyLead: "By using this tool, you agree to the",
    policy: "Usage Policy",
    invalid: "We couldn't sign you in. Check your username and password.",
  },
  es: {
    eyebrow: "ALSFRS-R AI",
    title: "Inicie sesión",
    username: "Usuario",
    password: "Contraseña",
    language: "Idioma de la evaluación",
    submit: "Iniciar sesión",
    loading: "Ingresando…",
    policyLead: "Al usar esta herramienta, acepta la",
    policy: "Política de uso",
    invalid: "No pudimos iniciar sesión. Revise su usuario y contraseña.",
  },
} as const;

export function LoginForm({ initialError = false }: { initialError?: boolean }) {
  const router = useRouter();
  const [locale, setLocale] = useState<Locale>("en");
  const [error, setError] = useState(initialError ? text.en.invalid : "");
  const [loading, setLoading] = useState(false);
  const t = text[locale];

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setLoading(true);
    const form = new FormData(event.currentTarget);
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: form.get("username"),
        password: form.get("password"),
        locale,
      }),
    }).catch(() => null);

    if (!response?.ok) {
      setError(t.invalid);
      setLoading(false);
      return;
    }
    router.replace("/dashboard");
    router.refresh();
  }

  return (
    <form className="login-form" method="post" action="/api/auth/login" onSubmit={submit}>
      <div>
        <p className="eyebrow">{t.eyebrow}</p>
        <h1>{t.title}</h1>
      </div>

      <label>
        <span>{t.username}</span>
        <input name="username" type="text" autoComplete="username" maxLength={64} required />
      </label>
      <label>
        <span>{t.password}</span>
        <input name="password" type="password" autoComplete="current-password" required />
      </label>
      <label>
        <span>{t.language}</span>
        <select name="locale" value={locale} onChange={(event) => setLocale(event.target.value as Locale)}>
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </label>

      {error && <p className="form-error" role="alert">{error}</p>}
      <button className="button primary wide" type="submit" disabled={loading}>
        {loading ? t.loading : t.submit}
      </button>
      <p className="policy-copy">
        {t.policyLead} <Link href="/usage-policy">{t.policy}</Link>.
      </p>
    </form>
  );
}
